import React, { memo, useMemo } from 'react';
import ConversationAvatar from './ConversationAvatar';
import Icon from '../AppIcon';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const messageTime = new Date(timestamp);
  const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
  return messageTime.toLocaleDateString();
};

/**
 * Memoized message bubble component with search highlighting.
 * Extracted from UnifiedMessagingPage to prevent remount on every parent render.
 */
const ChatMessageBubble = memo(({ message, currentUserId, isSearchResult, searchQuery }) => {
  const isOwn = message.senderId === currentUserId;
  const formattedTime = useMemo(() => formatTimestamp(message.timestamp), [message.timestamp]);
  const hasOriginalContext = message.metadata &&
    (message.metadata.original_job_id || message.metadata.original_conversation_id);

  const renderMessageContent = (content) => {
    if (!searchQuery?.trim() || typeof content !== 'string') {
      return content;
    }

    const query = searchQuery.toLowerCase();
    const contentLower = content.toLowerCase();

    if (!contentLower.includes(query)) {
      return content;
    }

    const parts = [];
    let lastIndex = 0;
    let currentIndex = contentLower.indexOf(query);

    while (currentIndex !== -1) {
      parts.push(content.substring(lastIndex, currentIndex));
      parts.push(
        <mark key={`highlight-${currentIndex}`} className="bg-yellow-300 text-inherit rounded px-0.5 font-semibold">
          {content.substring(currentIndex, currentIndex + query.length)}
        </mark>
      );
      lastIndex = currentIndex + query.length;
      currentIndex = contentLower.indexOf(query, lastIndex);
    }

    parts.push(content.substring(lastIndex));
    return parts;
  };

  return (
    <div
      data-message-id={message.id}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 sm:mb-4 transition-colors ${
        isSearchResult ? 'bg-yellow-50 rounded-lg p-2' : ''
      }`}
    >
      {!isOwn && (
        <ConversationAvatar
          profilePicture={message.senderAvatar}
          name={message.senderName}
          userId={message.senderId}
          size="w-6 h-6 sm:w-8 sm:h-8"
          textSize="text-xs sm:text-sm font-semibold"
          showOnlineStatus={true}
        />
      )}

      <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md ${isOwn ? 'order-1' : 'order-2'} ${!isOwn ? 'ml-1.5 sm:ml-2' : ''}`}>
        {/* Show original context badge for merged messages */}
        {hasOriginalContext && message.original_job_title && (
          <div className="mb-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded inline-flex items-center">
            <Icon name="Briefcase" size={10} className="mr-1" />
            {message.original_job_title}
          </div>
        )}

        <div
          className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base ${
            isOwn
              ? 'text-white rounded-br-sm'
              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
          } ${isSearchResult ? 'ring-2 ring-yellow-400 ring-offset-1' : ''}`}
          style={isOwn ? { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } : {}}
        >
          {message.type === 'interview_request' ? (
            <div className={`${isOwn ? 'bg-blue-700' : 'bg-blue-50'} border border-blue-200 rounded-lg p-3`}>
              <div className="flex items-center space-x-2 mb-2">
                <span className={`${isOwn ? 'text-blue-200' : 'text-blue-600'}`}>📅</span>
                <span className={`font-semibold ${isOwn ? 'text-blue-100' : 'text-blue-800'}`}>
                  {isOwn ? 'Interview Scheduled' : 'Interview Request'}
                </span>
              </div>
              <div className={`${isOwn ? 'text-blue-100' : 'text-gray-700'} whitespace-pre-line text-sm`}>
                {renderMessageContent(typeof message.content === 'string' ? message.content : '')}
              </div>
            </div>
          ) : (
            <p>{renderMessageContent(typeof message.content === 'string' ? message.content : '')}</p>
          )}
        </div>

        <div
          className={`mt-1 text-xs text-gray-500 flex items-center ${
            isOwn ? 'justify-end' : 'justify-start'
          }`}
        >
          {!isOwn && (
            <span className="font-medium mr-1">{message.senderName} •</span>
          )}
          <span>{formattedTime}</span>

          {/* Seen / Sent status for own messages */}
          {isOwn && (
            <span className={`ml-1.5 inline-flex items-center ${message.isRead ? 'text-blue-500' : 'text-gray-400'}`}>
              {message.isRead ? (
                <>
                  <Icon name="CheckCheck" size={14} />
                  <span className="ml-0.5">Seen</span>
                </>
              ) : (
                <Icon name="Check" size={14} />
              )}
            </span>
          )}

          {/* Show job context for merged conversations */}
          {hasOriginalContext && message.original_company_name && (
            <span className="ml-1 text-xs text-gray-400">
              • {message.original_company_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

ChatMessageBubble.displayName = 'ChatMessageBubble';
export default ChatMessageBubble;
